import { computed, inject } from '@angular/core'
import { toObservable } from '@angular/core/rxjs-interop'
import { CanActivateFn, Router, UrlTree } from '@angular/router'

import { Observable, map } from 'rxjs'

import { PermissionValue } from '@flowx/designer/modules/workspaces/shared/roles'
import { WorkspacesAndPermissionsStore } from '@flowx/designer/modules/workspaces/workspaces-and-permissions.store'
import { AiAvailabilityService } from '@flowx/designer/shared/services/ai-availability.service'

/** Every route guarded here lives under `/organisation-admin`, so only the org permissions count. */
export function DefaultOrganisationAdminPermissionsGuard(
  permissions: PermissionValue[]
): CanActivateFn {
  return (): Observable<boolean | UrlTree> => {
    const router = inject(Router)
    const workspacesAndPermissionsStore = inject(WorkspacesAndPermissionsStore)

    const isPermitted = computed(() => {
      const userOrganisationPermissions =
        workspacesAndPermissionsStore.userOrganisationPermissions()
      if (!permissions) {
        return false
      }
      return permissions.some((permission) => userOrganisationPermissions[permission])
    })

    return toObservable(isPermitted).pipe(
      map((isPermitted) => (isPermitted ? true : router.parseUrl('/403')))
    )
  }
}

// AI Settings pages are reachable only when at least one AI capability group
// (developer agents / runtime agents) is deployed in the environment
export function AiSettingsAvailabilityGuard(): CanActivateFn {
  return (): boolean | UrlTree => {
    const router = inject(Router)
    const aiAvailability = inject(AiAvailabilityService)

    return aiAvailability.anyAiAvailable() ? true : router.parseUrl('/403')
  }
}

/** The Agentic Harness page needs the developer agents, which the harness runs. */
export function DeveloperAgentsAvailabilityGuard(): CanActivateFn {
  return (): boolean | UrlTree => {
    const router = inject(Router)
    const aiAvailability = inject(AiAvailabilityService)

    return aiAvailability.developerAgentsAvailable() ? true : router.parseUrl('/403')
  }
}

/** The Defaults & Fallbacks page sets the models of the runtime agents only. */
export function RuntimeAgentsAvailabilityGuard(): CanActivateFn {
  return (): boolean | UrlTree => {
    const router = inject(Router)
    const aiAvailability = inject(AiAvailabilityService)

    return aiAvailability.runtimeAgentsAvailable() ? true : router.parseUrl('/403')
  }
}
